import { useCallback, useRef, useState } from 'react'

export type PlaceResult = {
  streetAddress: string
  city: string
  state: string
  postalCode: string
  country: string
  formattedAddress: string
}

export interface PlaceSuggestion {
  placeId: string
  description: string
}

interface AutocompleteSuggestion {
  placePrediction?: {
    placeId: string
    text?: { text: string }
    structuredFormat?: {
      mainText?: { text: string }
      secondaryText?: { text: string }
    }
  }
}

interface PlaceDetailsResponse {
  addressComponents?: Array<{
    longText: string
    shortText: string
    types: string[]
  }>
  formattedAddress?: string
}

interface UseGooglePlacesOptions {
  apiKey: string
  /** Restrict results to specific country codes (e.g., ['us']) */
  regionCodes?: string[]
  /** Debounce delay in ms (default: 300) */
  debounceMs?: number
}

const AUTOCOMPLETE_URL = 'https://places.googleapis.com/v1/places:autocomplete'
const PLACE_DETAILS_URL = 'https://places.googleapis.com/v1/places'

function parsePlaceDetails(place: PlaceDetailsResponse): PlaceResult {
  const components = place.addressComponents || []
  let streetNumber = ''
  let route = ''
  let city = ''
  let state = ''
  let postalCode = ''
  let country = ''

  for (const component of components) {
    const types = component.types
    if (types.includes('street_number')) {
      streetNumber = component.longText
    } else if (types.includes('route')) {
      route = component.longText
    } else if (types.includes('locality')) {
      city = component.longText
    } else if (types.includes('administrative_area_level_1')) {
      state = component.shortText
    } else if (types.includes('postal_code')) {
      postalCode = component.longText
    } else if (types.includes('country')) {
      country = component.shortText
    }
  }

  return {
    streetAddress: `${streetNumber} ${route}`.trim(),
    city,
    state,
    postalCode,
    country,
    formattedAddress: place.formattedAddress || '',
  }
}

export function useGooglePlaces({
  apiKey,
  regionCodes = ['us'],
  debounceMs = 300,
}: UseGooglePlacesOptions) {
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const fetchSuggestions = useCallback(
    (input: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (abortRef.current) abortRef.current.abort()

      if (!input || input.length < 3) {
        setSuggestions([])
        setLoading(false)
        return
      }

      setLoading(true)

      debounceRef.current = setTimeout(async () => {
        const controller = new AbortController()
        abortRef.current = controller

        try {
          const response = await fetch(AUTOCOMPLETE_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Goog-Api-Key': apiKey,
            },
            body: JSON.stringify({
              input,
              includedRegionCodes: regionCodes,
              includedPrimaryTypes: ['street_address', 'subpremise', 'premise'],
            }),
            signal: controller.signal,
          })

          if (!response.ok) {
            throw new Error(`Places autocomplete failed: ${response.status}`)
          }

          const data = await response.json()
          const results: PlaceSuggestion[] = (data.suggestions || [])
            .filter((s: AutocompleteSuggestion) => s.placePrediction)
            .map((s: AutocompleteSuggestion) => ({
              placeId: s.placePrediction?.placeId ?? '',
              description:
                s.placePrediction?.text?.text ||
                [
                  s.placePrediction?.structuredFormat?.mainText?.text,
                  s.placePrediction?.structuredFormat?.secondaryText?.text,
                ]
                  .filter(Boolean)
                  .join(', '),
            }))
            .filter((s: PlaceSuggestion) => s.placeId)

          setSuggestions(results)
          setError(null)
        } catch (err: any) {
          if (err.name !== 'AbortError') {
            setError(err.message)
            setSuggestions([])
          }
        } finally {
          setLoading(false)
        }
      }, debounceMs)
    },
    [apiKey, regionCodes, debounceMs]
  )

  const getPlaceDetails = useCallback(
    async (placeId: string): Promise<PlaceResult | null> => {
      try {
        const response = await fetch(`${PLACE_DETAILS_URL}/${placeId}`, {
          headers: {
            'X-Goog-Api-Key': apiKey,
            'X-Goog-FieldMask': 'addressComponents,formattedAddress',
          },
        })

        if (!response.ok) {
          throw new Error(`Place details failed: ${response.status}`)
        }

        const data: PlaceDetailsResponse = await response.json()
        return parsePlaceDetails(data)
      } catch (err: any) {
        setError(err.message)
        return null
      }
    },
    [apiKey]
  )

  const clearSuggestions = useCallback(() => {
    setSuggestions([])
  }, [])

  return { suggestions, loading, error, fetchSuggestions, getPlaceDetails, clearSuggestions }
}
